import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { consumePulseNext } from "./pulse/PulseGate";
import { Brain, RefreshCw, CheckCircle, XCircle } from "lucide-react";

export default function DirectLoginPage({ darkMode }) {
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState("");
  const dm = darkMode;

  useEffect(() => {
    const jwt = searchParams.get("jwt");
    if (!jwt) {
      setStatus("error");
      setError("No token found in URL.");
      return;
    }
    try {
      // Decode payload without verifying signature (validation happens server-side on next request)
      const payload = JSON.parse(atob(jwt.split(".")[1]));
      const user = { email: payload.email, is_admin: payload.is_admin ?? false };
      login(jwt, user);
      setStatus("success");
      const pulseNext = consumePulseNext();
      setTimeout(() => navigate(pulseNext || (user.is_admin ? "/admin" : "/feed")), 1200);
    } catch (e) {
      setStatus("error");
      setError("Invalid token format.");
    }
  }, []);

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-4 ${dm ? "bg-[#0c1011]" : "bg-[#f8f9fa]"}`}>
      <div className={`w-full max-w-md rounded-2xl p-10 text-center ${dm ? "bg-[#1a2122]" : "bg-white"}`}
        style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.15), 0 8px 40px rgba(25,28,29,0.08)" }}>

        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5
          ${status === "verifying" ? (dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]") :
            status === "success"   ? (dm ? "bg-emerald-400/10" : "bg-emerald-50") :
            (dm ? "bg-red-400/10" : "bg-red-50")}`}>
          {status === "verifying" && <RefreshCw className={`w-7 h-7 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />}
          {status === "success"   && <CheckCircle className="w-7 h-7 text-emerald-500" />}
          {status === "error"     && <XCircle className="w-7 h-7 text-red-500" />}
        </div>

        <h2 className={`editorial-heading text-2xl font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
          {status === "verifying" && "Signing you in…"}
          {status === "success"   && "Welcome, Admin."}
          {status === "error"     && "Login failed"}
        </h2>
        <p className={`text-sm ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
          {status === "verifying" && "Validating credentials…"}
          {status === "success"   && "Redirecting to your admin panel…"}
          {status === "error"     && (error || "The login link is invalid or expired.")}
        </p>

        {status === "error" && (
          <button onClick={() => navigate("/auth")}
            className={`mt-6 px-6 py-2.5 rounded-full category-tag text-sm transition-all ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
            Back to login
          </button>
        )}
      </div>
    </div>
  );
}
